import React, { useState, useEffect } from 'react';
import { BaseModal } from './BaseModal';

type OnboardingSystem = {
  id: number;
  platform: string;
  country: string;
  gma: string;
  manual: string;
  sfeu_insights: string;
  cognito: string;
  sales_center: string;
  sf_usa: string;
  checkmate: string;
  manual_eu: string;
  s4eu_alloy: string;
  mp_lbm: string;
  ltm: string;
  mp_insights: string;
  partner_portal: string;
  pp_insights: string;
  cm_lbm: string;
  tsys: string;
  monthly_statements: string;
  lt_lbm: string;
  lt_insights: string;
};

type ColumnGroup = {
  key: string;
  label: string;
};

type ColumnGroups = {
  boardingSystems: ColumnGroup[];
  underwritingSystems: ColumnGroup[];
  merchantPortal: ColumnGroup[];
  partnerPortal: ColumnGroup[];
  chargebackManagement: ColumnGroup[];
  monthlyStatements: ColumnGroup[];
  liveTransactions: ColumnGroup[];
};

type OnboardingDetailProps = {
  countryCode: string;
  countryName: string;
  onBack: () => void;
  theme?: 'light' | 'dark';
};

function OnboardingDetail({
  countryCode,
  countryName,
  onBack,
  theme: _theme = 'dark',
}: OnboardingDetailProps) {
  const [systems, setSystems] = useState<OnboardingSystem[]>([]);
  const [columnGroups, setColumnGroups] = useState<ColumnGroups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('boarding');

  useEffect(() => {
    const fetchOnboardingData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/onboarding?country=${countryCode}`);
        const result = await response.json();

        if (result.success) {
          setSystems(result.data);
          setColumnGroups(result.columnGroups);
        } else {
          setError(result.error || 'Failed to load onboarding data');
        }
      } catch (err) {
        setError('Failed to fetch onboarding data');
        console.error('Error fetching onboarding data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOnboardingData();
  }, [countryCode]);

  const renderCell = (value: string) => {
    if (value === 'Y') {
      return <span className="text-green-600 font-semibold">✓</span>;
    }
    return <span className="text-red-500 font-semibold">✗</span>;
  };

  // Define tabs based on column groups
  const tabs = [
    { id: 'boarding', label: 'Boarding Systems' },
    { id: 'underwriting', label: 'Underwriting' },
    { id: 'merchant', label: 'Merchant Portal' },
    { id: 'partner', label: 'Partner Portal' },
    { id: 'chargeback', label: 'Chargeback' },
    { id: 'statements', label: 'Statements' },
    { id: 'transactions', label: 'Live Transactions' },
  ];

  // Filter columns based on active tab
  const getVisibleColumns = () => {
    if (!columnGroups) return [];

    switch (activeTab) {
      case 'boarding':
        return columnGroups.boardingSystems;
      case 'underwriting':
        return columnGroups.underwritingSystems;
      case 'merchant':
        return columnGroups.merchantPortal;
      case 'partner':
        return columnGroups.partnerPortal;
      case 'chargeback':
        return columnGroups.chargebackManagement;
      case 'statements':
        return columnGroups.monthlyStatements;
      case 'transactions':
        return columnGroups.liveTransactions;
      default:
        return columnGroups.boardingSystems;
    }
  };

  const visibleColumns = getVisibleColumns();

  // Get flag emoji for the country
  const getFlagEmoji = () => {
    const flagMap: Record<string, string> = {
      US: '🇺🇸',
      Canada: '🇨🇦',
      Germany: '🇩🇪',
      UK: '🇬🇧',
      France: '🇫🇷',
      Spain: '🇪🇸',
      Italy: '🇮🇹',
      Netherlands: '🇳🇱',
      Belgium: '🇧🇪',
      Austria: '🇦🇹',
      Switzerland: '🇨🇭',
      Poland: '🇵🇱',
      'Czech Republic': '🇨🇿',
      Ireland: '🇮🇪',
      Portugal: '🇵🇹',
      Sweden: '🇸🇪',
      Denmark: '🇩🇰',
      Finland: '🇫🇮',
      Norway: '🇳🇴',
    };
    return flagMap[countryName] || '🌐';
  };

  if (loading) {
    return (
      <BaseModal
        isOpen={true}
        onClose={onBack}
        title={`${getFlagEmoji()} ${countryName}`}
        subtitle="Onboarding & Operational Systems"
        maxWidth="5xl"
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground"></div>
        </div>
      </BaseModal>
    );
  }

  if (error) {
    return (
      <BaseModal
        isOpen={true}
        onClose={onBack}
        title={`${getFlagEmoji()} ${countryName}`}
        subtitle="Onboarding & Operational Systems"
        maxWidth="5xl"
      >
        <div className="text-center py-12">
          <p className="text-xl mb-4 text-foreground">Error loading onboarding data</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Go Back
          </button>
        </div>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen={true}
      onClose={onBack}
      title={`${getFlagEmoji()} ${countryName}`}
      subtitle="Onboarding & Operational Systems"
      maxWidth="5xl"
      className="max-h-[90vh]"
    >
      <div className="overflow-y-auto max-h-[70vh] -mx-6 px-6">
        {/* Sticky Tabs */}
        <div className="sticky top-0 z-20 bg-card pb-2 -mx-6 px-6">
          {/* Tab Navigation */}
          <nav className="flex items-center gap-2 pt-4 mb-4 bg-card overflow-x-auto scrollbar-thin">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Table Header - Desktop Only */}
          <div className="hidden md:block overflow-x-auto">
            <div className="inline-block min-w-full">
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <thead className="bg-card">
                  <tr className="h-16 border-b border-border">
                    <th
                      className="text-left px-4 py-2 text-sm font-medium text-foreground bg-card"
                      style={{ width: '300px' }}
                    ></th>
                    {visibleColumns.map(col => (
                      <th
                        key={col.key}
                        className="text-center px-1 py-2 font-medium text-foreground bg-card cursor-default select-none"
                        style={{ width: '80px' }}
                        title={col.label}
                      >
                        <div className="flex flex-col items-center justify-center h-full">
                          <span className="text-xs select-none whitespace-nowrap">{col.label}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="space-y-6">
          {/* Mobile Card View (< md) */}
          <div className="block md:hidden space-y-4">
            {systems.map(system => (
              <div
                key={system.id}
                className="bg-card border border-border rounded-lg p-4 shadow-sm"
              >
                <h3 className="font-semibold text-foreground text-base mb-4 pb-3 border-b border-border">
                  {system.platform}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {visibleColumns.map(col => (
                    <div key={col.key} className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{col.label}</span>
                      <span className="ml-2">
                        {renderCell(system[col.key as keyof OnboardingSystem] as string)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View (>= md) */}
          <div className="hidden md:block bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full min-w-[600px]" style={{ tableLayout: 'fixed' }}>
              <tbody>
                {systems.map(system => (
                  <tr
                    key={system.id}
                    className="border-b border-border hover:bg-accent/50 hover:shadow-sm transition-colors"
                  >
                    <td className="p-4" style={{ width: '300px' }}>
                      <div className="font-medium text-foreground text-sm leading-tight">
                        {system.platform}
                      </div>
                    </td>
                    {visibleColumns.map(col => (
                      <td key={col.key} className="p-4 text-center" style={{ width: '80px' }}>
                        {renderCell(system[col.key as keyof OnboardingSystem] as string)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default OnboardingDetail;
